import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Container,
    Paper,
    Title,
    Text,
    Button,
    Group,
    Stack,
    Alert,
    Select,
    TextInput,
    Badge,
    Card,
    Divider,
    Box
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconCreditCard, IconX } from "@tabler/icons-react";
import API_BASE_URL from "../config";

function Payment() {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);
    const [error, setError] = useState("");
    
    // 결제 폼 상태
    const [paymentProvider, setPaymentProvider] = useState("kakaopay");
    const [theaterLocation, setTheaterLocation] = useState("강남점");
    const [cardNumber, setCardNumber] = useState("");
    const [cardExpiry, setCardExpiry] = useState("");
    const [cardCvc, setCardCvc] = useState("");
    
    // 예약 정보 (이전 페이지에서 전달받음)
    const reservationData = location.state || {
        selectedSeats: [1],
        seatDetails: [{ id: 1, seatNumber: "A1" }],
        movieTitle: "테스트 영화",
        totalAmount: 15000,
        username: localStorage.getItem('username') || 'testuser'
    };

    const paymentProviders = [
        { value: "kakaopay", label: "카카오페이" },
        { value: "naverpay", label: "네이버페이" },
        { value: "creditcard", label: "신용카드" }
    ];

    const theaterLocations = [
        { value: "강남점", label: "강남점" },
        { value: "홍대점", label: "홍대점" },
        { value: "신촌점", label: "신촌점" },
        { value: "명동점", label: "명동점" },
        { value: "잠실점", label: "잠실점" },
        { value: "건대점", label: "건대점" },
        { value: "노원점", label: "노원점" },
        { value: "분당점", label: "분당점" },
        { value: "일산점", label: "일산점" },
        { value: "수원점", label: "수원점" }
    ];

    const handlePayment = async () => {
        setLoading(true);
        setError("");
        setPaymentResult(null);

        try {
            const paymentRequest = {
                selectedSeats: reservationData.selectedSeats,
                seatDetails: reservationData.seatDetails,
                username: reservationData.username,
                totalAmount: reservationData.totalAmount,
                paymentProvider: paymentProvider,
                theaterLocation: theaterLocation,
                cardNumber: paymentProvider === "creditcard" ? cardNumber : null,
                cardExpiry: paymentProvider === "creditcard" ? cardExpiry : null,
                cardCvc: paymentProvider === "creditcard" ? cardCvc : null,
                movieTitle: reservationData.movieTitle,
                userAgent: navigator.userAgent,
                clientIp: "127.0.0.1" // 실제로는 서버에서 설정
            };

            console.log("🎯 DI 데모: 다중 좌석 결제 요청", paymentRequest);
            
            const response = await axios.post(
                `${API_BASE_URL}/apm-demo/payment/process`,
                paymentRequest
            );

            setPaymentResult(response.data);
            
            // 성공 시 3초 후 예약 확인 페이지로 이동
            if (response.data.status === "SUCCESS") {
                setTimeout(() => {
                    navigate('/check-reservations');
                }, 3000);
            }

        } catch (err) {
            console.error("Payment failed:", err);
            setError(err.response?.data?.message || "결제 처리 중 오류가 발생했습니다.");
            
            // 실패한 경우에도 응답 데이터가 있으면 표시
            if (err.response?.data) {
                setPaymentResult(err.response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "SUCCESS": return "green";
            case "FAILED": return "red";
            case "PENDING": return "yellow";
            case "CANCELLED": return "gray";
            default: return "blue";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "SUCCESS": return <IconCheck size={16} />;
            case "FAILED": return <IconX size={16} />;
            default: return <IconCreditCard size={16} />;
        }
    };

    return (
        <Container size="sm" py="xl">
            <Paper shadow="md" p="xl" style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                <Title order={2} align="center" mb="xl" style={{ color: 'white' }}>
                    결제하기
                </Title>

                {/* 예약 정보 */}
                <Card mb="xl" style={{ backgroundColor: '#2a2a2a', border: '1px solid #444' }}>
                    <Text size="lg" weight={500} mb="sm" style={{ color: 'white' }}>
                        예약 정보
                    </Text>
                    <Stack spacing="xs">
                        <Group position="apart">
                            <Text color="dimmed">영화</Text>
                            <Text style={{ color: 'white' }}>{reservationData.movieTitle}</Text>
                        </Group>
                        <Group position="apart">
                            <Text color="dimmed">영화관</Text>
                            <Text style={{ color: 'white' }}>{theaterLocation}</Text>
                        </Group>
                        <Group position="apart">
                            <Text color="dimmed">좌석</Text>
                            <Text style={{ color: 'white' }}>
                                {reservationData.seatDetails?.map(seat => seat.seatNumber).join(', ') || 'Unknown'}
                                {reservationData.seatDetails?.length > 1 && ` (${reservationData.seatDetails.length}개)`}
                            </Text>
                        </Group>
                        <Group position="apart">
                            <Text color="dimmed">사용자</Text>
                            <Text style={{ color: 'white' }}>{reservationData.username}</Text>
                        </Group>
                        <Divider />
                        <Group position="apart">
                            <Text size="lg" weight={500} color="white">총 결제금액</Text>
                            <Text size="lg" weight={500} style={{ color: '#0071e3' }}>
                                {reservationData.totalAmount?.toLocaleString()}원
                            </Text>
                        </Group>
                    </Stack>
                </Card>

                {!paymentResult && (
                    <Stack spacing="md">
                        {/* 영화관 선택 */}
                        <Select
                            label="영화관"
                            placeholder="영화관을 선택하세요"
                            value={theaterLocation}
                            onChange={setTheaterLocation}
                            data={theaterLocations}
                            styles={{
                                input: { backgroundColor: '#2a2a2a', borderColor: '#444', color: 'white' },
                                label: { color: 'white' }
                            }}
                        />

                        {/* 결제 수단 선택 */}
                        <Select
                            label="결제 수단"
                            placeholder="결제 수단을 선택하세요"
                            value={paymentProvider}
                            onChange={setPaymentProvider}
                            data={paymentProviders}
                            styles={{
                                input: { backgroundColor: '#2a2a2a', borderColor: '#444', color: 'white' },
                                label: { color: 'white' }
                            }}
                        />

                        {/* 신용카드 정보 입력 */}
                        {paymentProvider === "creditcard" && (
                            <Stack spacing="sm">
                                <TextInput
                                    label="카드 번호"
                                    placeholder="1234-5678-9012-3456"
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(e.target.value)}
                                    styles={{
                                        input: { backgroundColor: '#2a2a2a', borderColor: '#444', color: 'white' },
                                        label: { color: 'white' }
                                    }}
                                />
                                <Group grow>
                                    <TextInput
                                        label="유효기간"
                                        placeholder="MM/YY"
                                        value={cardExpiry}
                                        onChange={(e) => setCardExpiry(e.target.value)}
                                        styles={{
                                            input: { backgroundColor: '#2a2a2a', borderColor: '#444', color: 'white' },
                                            label: { color: 'white' }
                                        }}
                                    />
                                    <TextInput
                                        label="CVC"
                                        placeholder="123"
                                        value={cardCvc}
                                        onChange={(e) => setCardCvc(e.target.value)}
                                        styles={{
                                            input: { backgroundColor: '#2a2a2a', borderColor: '#444', color: 'white' },
                                            label: { color: 'white' }
                                        }}
                                    />
                                </Group>
                            </Stack>
                        )}

                        {error && (
                            <Alert icon={<IconAlertCircle size={16} />} title="결제 실패" color="red">
                                {error}
                            </Alert>
                        )}

                        <Group position="center" mt="xl">
                            <Button
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={loading}
                            >
                                취소
                            </Button>
                            <Button
                                onClick={handlePayment}
                                loading={loading}
                                size="lg"
                                style={{ backgroundColor: '#0071e3' }}
                            >
                                {loading ? "결제 처리 중..." : `${reservationData.totalAmount?.toLocaleString()}원 결제하기`}
                            </Button>
                        </Group>
                    </Stack>
                )}

                {/* 결제 결과 */}
                {paymentResult && (
                    <Card style={{ backgroundColor: '#2a2a2a', border: '1px solid #444' }}>
                        <Group position="center" mb="md">
                            <Badge
                                color={getStatusColor(paymentResult.status)}
                                size="lg"
                                leftSection={getStatusIcon(paymentResult.status)}
                            >
                                {paymentResult.status}
                            </Badge>
                        </Group>

                        <Text align="center" size="lg" mb="md" style={{ color: 'white' }}>
                            {paymentResult.message}
                        </Text>

                        {paymentResult.paymentId && (
                            <Text align="center" size="sm" color="dimmed" mb="md">
                                결제 ID: {paymentResult.paymentId}
                            </Text>
                        )}

                        {paymentResult.processingTimeMs && (
                            <Text align="center" size="sm" color="dimmed" mb="md">
                                처리 시간: {paymentResult.processingTimeMs}ms
                            </Text>
                        )}

                        {paymentResult.errorCode && (
                            <Alert color="red" mb="md">
                                <Text size="sm">오류 코드: {paymentResult.errorCode}</Text>
                                {paymentResult.errorDetail && (
                                    <Text size="xs" color="dimmed">{paymentResult.errorDetail}</Text>
                                )}
                            </Alert>
                        )}

                        {paymentResult.processingSteps && paymentResult.processingSteps.length > 0 && (
                            <Box mt="md">
                                <Text size="sm" weight={500} mb="xs" style={{ color: 'white' }}>
                                    처리 단계:
                                </Text>
                                <Stack spacing={2}>
                                    {paymentResult.processingSteps.map((step, index) => (
                                        <Text key={index} size="xs" color="dimmed">
                                            {index + 1}. {step}
                                        </Text>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Group position="center" mt="xl">
                            {paymentResult.status === "SUCCESS" ? (
                                <Text size="sm" color="dimmed" align="center">
                                    3초 후 예약 확인 페이지로 이동합니다...
                                </Text>
                            ) : (
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setPaymentResult(null);
                                        setError("");
                                    }}
                                >
                                    다시 시도
                                </Button>
                            )}
                        </Group>
                    </Card>
                )}
            </Paper>
        </Container>
    );
}

export default Payment;